import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { sessionListSearchValueAtom } from '@/atoms/sideNavAtoms';
import { createSessionFuseSettings } from '@/components/layout/Navigation/sessions/constants';
import {
  isUrgentOnlyFilterAtom,
  selectedDateRangeFilterAtom,
  selectedPatientProfilesFilterAtom,
  selectedStatusesFilterAtom,
} from '@/components/layout/Navigation/sessions/header/toolbar/filters/atoms';
import { useIsSessionUrgencyEnabled } from '@/features/flags';
import { useQueryCombinedSessions } from '@/hooks/query/useQueryCombinedSessions';
import { useTranslations } from '@heidi/runtime/i18n';
import Fuse from 'fuse.js';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

const hasDateRangeFilter = (dateRange: { from?: Date; to?: Date }) =>
  Boolean(dateRange.from || dateRange.to);

const hasServerSideSessionListFilters = ({
  searchValue,
  selectedDateRange,
  selectedPatientProfiles,
  selectedStatuses,
  isUrgentOnly,
}: {
  searchValue: string;
  selectedDateRange: { from?: Date; to?: Date };
  selectedPatientProfiles: string[];
  selectedStatuses: unknown[];
  isUrgentOnly: boolean;
}) =>
  Boolean(
    searchValue.trim() ||
    selectedStatuses.length > 0 ||
    hasDateRangeFilter(selectedDateRange) ||
    selectedPatientProfiles.length > 0 ||
    isUrgentOnly
  );

/**
 * Shared hook for session search functionality.
 * Handles fetching sessions (v1/v2), creating Fuse.js index, and filtering by search value.
 * Used by the sessions list views that render filtered online session results.
 */
export const useSessionSearch = () => {
  const { t } = useTranslations();
  const searchValue = useAtomValue(sessionListSearchValueAtom);

  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const selectedDateRange = useAtomValue(selectedDateRangeFilterAtom);
  const selectedPatientProfiles = useAtomValue(selectedPatientProfilesFilterAtom);
  const selectedStatuses = useAtomValue(selectedStatusesFilterAtom);
  const rawIsUrgentOnly = useAtomValue(isUrgentOnlyFilterAtom);
  const isSessionUrgencyEnabled = useIsSessionUrgencyEnabled();
  const isUrgentOnly = isSessionUrgencyEnabled && rawIsUrgentOnly;
  const suppressOfflineSyntheticSessions =
    sessionQueryVersion === 'v3' &&
    hasServerSideSessionListFilters({
      searchValue,
      selectedDateRange,
      selectedPatientProfiles,
      selectedStatuses,
      isUrgentOnly,
    });

  const {
    refetch: refetchSessions,
    isPending: isPendingSessions,
    sessions: onlineSessions,
    sessionsHiddenByQueuedDelete,
    totalMatchCount,
  } = useQueryCombinedSessions({ suppressOfflineSyntheticSessions });

  const untitledSessionText = t('shared.UntitledSession');
  const sessionFuse = useMemo(
    () => new Fuse(onlineSessions || [], createSessionFuseSettings(untitledSessionText)),
    [onlineSessions, untitledSessionText]
  );

  const sessionSearchResult = useMemo(() => {
    if (sessionQueryVersion === 'v3') {
      return onlineSessions;
    }
    return searchValue
      ? sessionFuse.search(searchValue).map((result) => result.item)
      : onlineSessions;
  }, [sessionQueryVersion, searchValue, sessionFuse, onlineSessions]);

  return {
    searchValue,
    onlineSessions,
    // Rows the queued-delete read filter removed upstream, passed through
    // unsearched. Only the EHR appointment link check may read these — see
    // `onlyPendingServerDeletes`. Anything rendering a list wants
    // `sessionSearchResult`.
    sessionsHiddenByQueuedDelete,
    sessionSearchResult,
    refetchSessions,
    isPendingSessions,
    totalMatchCount,
  };
};
